import {
  createContext,
  use,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useEditorFieldHeaderVisibility } from "@/features/sparxsEditor/hooks/editor/useEditorFieldHeaderVisibility";

export interface EditorDrawerNavigationContextValue {
  closeAndScrollToPreview: () => void;
  scrollDrawerToTop: () => void;
  headerVisible: boolean;
  registerFieldHeader: (element: HTMLElement | null) => void;
  drawerAnchorRef: RefObject<HTMLElement | null>;
}

const EditorDrawerNavigationContext =
  createContext<EditorDrawerNavigationContextValue | null>(null);

export const useEditorDrawerNavigation =
  (): EditorDrawerNavigationContextValue | null =>
    use(EditorDrawerNavigationContext);

export interface EditorDrawerNavigationProviderProps {
  closeAndScrollToPreview: () => void;
  scrollDrawerToTop: () => void;
  drawerAnchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
}

export const EditorDrawerNavigationProvider = ({
  closeAndScrollToPreview,
  scrollDrawerToTop,
  drawerAnchorRef,
  children,
}: EditorDrawerNavigationProviderProps) => {
  const [fieldHeaderElement, setFieldHeaderElement] =
    useState<HTMLElement | null>(null);
  const headerVisible = useEditorFieldHeaderVisibility(fieldHeaderElement);

  const registerFieldHeader = useCallback((element: HTMLElement | null) => {
    setFieldHeaderElement(element);
  }, []);

  const value = useMemo(
    (): EditorDrawerNavigationContextValue => ({
      closeAndScrollToPreview,
      scrollDrawerToTop,
      headerVisible,
      registerFieldHeader,
      drawerAnchorRef,
    }),
    [
      closeAndScrollToPreview,
      scrollDrawerToTop,
      headerVisible,
      registerFieldHeader,
      drawerAnchorRef,
    ],
  );

  return (
    <EditorDrawerNavigationContext value={value}>
      {children}
    </EditorDrawerNavigationContext>
  );
};
